import React, { useMemo, useState } from "react";
import { Link2, MessageSquareText, Copy, Check, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { getPmsDisplayName } from "../../utils/onboarding";
import {
  trackHostOnboardingPropertyUrlCopied,
  trackHostOnboardingTemplateCopied,
} from "../../utils/hostAnalytics";

export const DEFAULT_GUEST_MESSAGE_TEMPLATE = `Hi {{guest_name}},

To make your arrival seamless, you can pre-order groceries and request additional services for your stay.

Order here:
{{bellhopt_url}}

Everything will be delivered and coordinated for your arrival.

Let me know if you need anything.`;

export const HOSPITABLE_GUEST_MESSAGE_TEMPLATE = `Hi %guest_first_name%,

To make your arrival seamless, you can pre-order groceries and request additional services for your stay.

Order here:
%guest_portal_bellhopt%

Everything will be delivered and coordinated for your arrival.

Let me know if you need anything.`;

function copyToClipboard(text) {
  if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
    return navigator.clipboard.writeText(text);
  }
  const textArea = document.createElement("textarea");
  textArea.value = text;
  document.body.appendChild(textArea);
  textArea.select();
  try {
    document.execCommand("copy");
  } catch (_) {
    /* no-op */
  }
  document.body.removeChild(textArea);
  return Promise.resolve();
}

const providerGuidance = {
  guesty: [
    "Open the automated guest message you use before arrival.",
    "Add a custom field for each property link if your Guesty workspace supports custom variables.",
    "Insert {{bellhopt_url}} where the Bellhopt order link should appear.",
  ],
  streamline: [
    "Open the message template or automated email used for pre-arrival instructions.",
    "Create a custom field or merge tag for the Bellhopt URL if available in your Streamline setup.",
    "Place {{bellhopt_url}} in the message body and map it to the right property link.",
  ],
  hospitable: [
    "Open the relevant automated message in Hospitable.",
    "Ensure your Hospitable message template supports Bellhopt shortcode enrichment for guest links.",
    "Insert %guest_portal_bellhopt% in the message where guests should start their order.",
  ],
  track: [
    "Create or identify the Track custom field that should store each Bellhopt property URL.",
    "Connect Track in Bellhopt with that custom field handle so Bellhopt can write links back to Track automatically.",
    "Reference that Track custom field in your pre-arrival guest message template.",
  ],
  generic: [
    "Open the automated pre-arrival message your team already sends to guests.",
    "Create a property-specific custom field named Bellhopt URL if your platform supports custom fields.",
    "Insert {{bellhopt_url}} in the template and map that variable to each property’s Bellhopt link.",
  ],
};

function MessagingSetupGuide({
  hostId,
  properties = [],
  selectedPms,
  source = "onboarding",
  compact = false,
  stage = false,
}) {
  const [copiedTemplate, setCopiedTemplate] = useState(false);
  const [copiedPropertyId, setCopiedPropertyId] = useState("");

  const activeProperties = useMemo(
    () => (Array.isArray(properties) ? properties.filter((property) => !property?.isDeleted) : []),
    [properties]
  );
  const primaryProperty = activeProperties[0];
  const guidance = providerGuidance[selectedPms] || providerGuidance.generic;
  const providerName = selectedPms ? getPmsDisplayName(selectedPms) : "your messaging platform";
  const variableToken = selectedPms === "hospitable" ? "%guest_portal_bellhopt%" : "{{bellhopt_url}}";
  const templateToCopy = selectedPms === "hospitable"
    ? HOSPITABLE_GUEST_MESSAGE_TEMPLATE
    : DEFAULT_GUEST_MESSAGE_TEMPLATE;

  const handleCopyTemplate = () => {
    copyToClipboard(templateToCopy).then(() => {
      setCopiedTemplate(true);
      trackHostOnboardingTemplateCopied({ hostId, source, provider: selectedPms });
      setTimeout(() => setCopiedTemplate(false), 2500);
    });
  };

  const handleCopyPropertyUrl = (property) => {
    if (!property?.uniqueLink) return;
    copyToClipboard(property.uniqueLink).then(() => {
      setCopiedPropertyId(property._id);
      trackHostOnboardingPropertyUrlCopied({
        hostId,
        source,
        provider: selectedPms,
        propertyId: property._id,
      });
      setTimeout(() => setCopiedPropertyId(""), 2500);
    });
  };

  if (stage) {
    return (
      <div className="grid h-full min-h-0 gap-5 lg:grid-cols-[0.9fr_1.1fr]">
        <section className="flex min-h-0 flex-col rounded-[26px] bg-gray-950 p-5 text-white shadow-2xl shadow-gray-950/20">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-gray-950">
              <Link2 size={20} />
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-white/50">Property URL</p>
              <h3 className="text-lg font-semibold">Guest link</h3>
            </div>
          </div>

          {primaryProperty ? (
            <div className="mt-6 flex min-h-0 flex-1 flex-col">
              <p className="truncate text-sm font-semibold text-white/60">
                {primaryProperty.listingTitle || primaryProperty.address?.line1 || "Primary property"}
              </p>
              <div className="mt-3 rounded-2xl border border-white/10 bg-white/10 p-4 font-mono text-sm leading-6 text-white shadow-inner">
                <span className="break-all">{primaryProperty.uniqueLink || "Link not available yet"}</span>
              </div>
              <button
                type="button"
                onClick={() => handleCopyPropertyUrl(primaryProperty)}
                disabled={!primaryProperty.uniqueLink}
                className="mt-4 inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl bg-white px-4 py-2.5 text-sm font-bold text-gray-950 transition hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {copiedPropertyId === primaryProperty._id ? <Check size={16} /> : <Copy size={16} />}
                {copiedPropertyId === primaryProperty._id ? "Copied" : "Copy property URL"}
              </button>

              {activeProperties.length > 1 && (
                <div className="mt-4 rounded-2xl border border-white/10">
                  {activeProperties.slice(1).map((property) => (
                    <button
                      key={property._id}
                      type="button"
                      onClick={() => handleCopyPropertyUrl(property)}
                      disabled={!property.uniqueLink}
                      className="flex w-full items-center justify-between gap-3 border-b border-white/10 px-4 py-3 text-left last:border-b-0 disabled:opacity-50"
                    >
                      <span className="min-w-0 truncate text-sm font-semibold text-white/75">
                        {property.listingTitle || property.address?.line1 || "Property"}
                      </span>
                      <span className="text-xs font-bold text-white">
                        {copiedPropertyId === property._id ? "Copied" : "Copy"}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="mt-6 flex min-h-[210px] flex-1 items-center justify-center rounded-2xl border border-dashed border-white/20 text-center">
              <p className="max-w-xs text-sm leading-6 text-white/60">
                Add or sync a property first. Bellhopt creates one unique URL per active property.
              </p>
            </div>
          )}
        </section>

        <section className="grid min-h-0 gap-5 md:grid-rows-[auto_1fr]">
          <div>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gray-100 text-gray-700">
                <MessageSquareText size={18} />
              </div>
              <div>
                <h3 className="text-sm font-bold text-gray-950">Custom-field pattern</h3>
                <p className="text-sm text-gray-500">
                  Use this as a safe fallback for {providerName}; provider screens may label fields differently.
                </p>
              </div>
            </div>

            <div className="mt-5 grid gap-0 overflow-hidden rounded-2xl border border-gray-200 bg-white md:grid-cols-3">
              {["Create Bellhopt URL", "Map property link", "Insert variable"].map((title, index) => (
                <motion.div
                  key={title}
                  initial={{ opacity: 0, x: 12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.08 }}
                  className="border-b border-gray-200 p-4 last:border-b-0 md:border-b-0 md:border-r md:last:border-r-0"
                >
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-gray-400">0{index + 1}</p>
                  <h4 className="mt-3 text-sm font-bold text-gray-950">{title}</h4>
                  <p className="mt-2 text-xs font-medium leading-5 text-gray-600">{guidance[index]}</p>
                </motion.div>
              ))}
            </div>
          </div>

          <div>
            <div className="mb-3 flex items-center justify-between gap-3">
              <div>
                <h3 className="text-sm font-bold text-gray-950">Guest message template</h3>
                <p className="mt-1 text-sm text-gray-500">Paste into your automated pre-arrival message.</p>
              </div>
              <button
                type="button"
                onClick={handleCopyTemplate}
                className="inline-flex min-h-[40px] items-center gap-1.5 rounded-xl bg-gray-950 px-4 py-2 text-sm font-bold text-white transition hover:bg-gray-800"
              >
                {copiedTemplate ? <Check size={15} /> : <Copy size={15} />}
                {copiedTemplate ? "Copied" : "Copy"}
              </button>
            </div>
            <pre className="whitespace-pre-wrap rounded-2xl border border-gray-200 bg-white p-4 text-sm font-medium leading-6 text-gray-800">
              {templateToCopy}
            </pre>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <section className="rounded-3xl border border-gray-200 bg-white p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-primary-500">Recommended</p>
            <h2 className="mt-1 text-xl font-bold text-gray-900">Add Bellhopt to guest messaging</h2>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-gray-500">
              Activation can continue without this step, but guests discover Bellhopt when the property link appears in your automated arrival messaging.
            </p>
          </div>
          <div className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-semibold text-gray-700">
            Variable: <span className="font-mono text-gray-900">{variableToken}</span>
          </div>
        </div>
      </section>

      <section className="rounded-3xl border border-gray-200 bg-white p-5">
        <div className="mb-4 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gray-100 text-gray-700">
            <Link2 size={18} />
          </div>
          <div>
            <h3 className="text-sm font-bold text-gray-900">Property URLs</h3>
            <p className="text-sm text-gray-500">Copy each link into the matching property field or message template.</p>
          </div>
        </div>

        {activeProperties.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50 p-6 text-center">
            <p className="text-sm text-gray-500">Add or sync a property first. Bellhopt creates a unique URL for each property.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {activeProperties.map((property) => (
              <div
                key={property._id}
                className="grid grid-cols-1 items-center gap-3 rounded-2xl border border-gray-200 bg-gray-50 p-3 md:grid-cols-[1fr_minmax(220px,360px)_auto]"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-bold text-gray-900">
                    {property.listingTitle || property.address?.line1 || "Property"}
                  </p>
                  <p className="truncate text-xs text-gray-500">
                    {[property.address?.line1, property.address?.city, property.address?.state].filter(Boolean).join(", ")}
                  </p>
                </div>
                <div className="min-w-0 rounded-xl bg-white px-3 py-2 font-mono text-xs text-gray-700">
                  <span className="block truncate">{property.uniqueLink || "Link not available yet"}</span>
                </div>
                <button
                  type="button"
                  onClick={() => handleCopyPropertyUrl(property)}
                  disabled={!property.uniqueLink}
                  className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-gray-900 px-4 py-2 text-sm font-bold text-white transition hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {copiedPropertyId === property._id ? <Check size={15} /> : <Copy size={15} />}
                  {copiedPropertyId === property._id ? "Copied" : "Copy"}
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      {!compact && (
        <section className="overflow-hidden rounded-3xl border border-gray-200 bg-white p-5">
          <div className="mb-5 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gray-100 text-gray-700">
              <MessageSquareText size={18} />
            </div>
            <div>
              <h3 className="text-sm font-bold text-gray-900">Guided setup pattern</h3>
              <p className="text-sm text-gray-500">
                Use this as a safe fallback for {providerName}; provider-specific screens may label fields differently.
              </p>
            </div>
          </div>

          <div className="grid gap-3 md:grid-cols-3">
            {["Create field", "Map property URL", "Insert variable"].map((title, index) => (
              <motion.div
                key={title}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.08 }}
                className="relative rounded-2xl border border-gray-200 bg-gray-50 p-4"
              >
                <div className="mb-3 flex items-center justify-between">
                  <span className="text-xs font-bold uppercase tracking-wide text-gray-400">0{index + 1}</span>
                  {index < 2 && <ArrowRight size={16} className="hidden text-gray-300 md:block" />}
                </div>
                <h4 className="text-sm font-bold text-gray-900">{title}</h4>
                <p className="mt-2 text-sm leading-6 text-gray-500">{guidance[index]}</p>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      <section className="rounded-3xl border border-gray-200 bg-white p-5">
        <div className="mb-3 flex items-center justify-between gap-3">
          <div>
            <h3 className="text-sm font-bold text-gray-900">Guest message template</h3>
            <p className="mt-1 text-sm text-gray-500">A polished default you can paste into your automated guest message.</p>
          </div>
          <button
            type="button"
            onClick={handleCopyTemplate}
            className="inline-flex items-center gap-1.5 rounded-xl bg-gray-900 px-4 py-2 text-sm font-bold text-white transition hover:bg-gray-800"
          >
            {copiedTemplate ? <Check size={15} /> : <Copy size={15} />}
            {copiedTemplate ? "Copied" : "Copy"}
          </button>
        </div>
        <pre className="whitespace-pre-wrap rounded-2xl border border-gray-200 bg-gray-50 p-4 text-sm leading-6 text-gray-800">
          {templateToCopy}
        </pre>
      </section>
    </div>
  );
}

export default MessagingSetupGuide;
